// ED-RK-005: what a read serves from the phone's saved copy carries when that copy was read, so a screen can say so and how
// old it is, and never show it as if it had just been read. The tag is kept by the served list itself (not by the read's
// key), so a later answer is never taken for the copy, and the copy never for an answer.
//
// It holds only while the list reaches the screen as it was served: a `select` on the read, or `setQueryData` on its key,
// makes a new list without the tag, and the copy would pass as current. Neither is used on these reads; keep it that way.
import { replaceEqualDeep } from "@tanstack/react-query";

/** When a saved copy was read (milliseconds since 1970), or "unknown" when the phone holds it but not its time. */
export type CopyTime = number | "unknown";

const copies = new WeakMap<object, CopyTime>();
const isCopy = (data: unknown) => typeof data === "object" && data !== null && copies.has(data);

/** Marks what a read serves from the phone's saved copy (a list, or a summary) with when it was read. An empty list is not marked. */
export function markSavedCopy<T extends object>(saved: T, at: CopyTime): T {
  if (!Array.isArray(saved) || saved.length) copies.set(saved, at);
  return saved;
}

/**
 * The `structuralSharing` of a read that can serve a saved copy: an answer with the same rows as the copy before it (or
 * after it) is not merged into it, or the copy's tag would stay on the answer (or the answer pass for the copy).
 */
export function copySharing(old: unknown, next: unknown): unknown {
  return isCopy(old) || isCopy(next) ? next : replaceEqualDeep(old, next);
}

/**
 * When what a read shows was read, if it is not the answer just read: the phone's saved copy (its time, or "unknown"), or
 * the rows a re-read that failed has left on screen (when they were read). Null when it is the answer just read. Fits any
 * read (useOfflineRows, useAttention, useRows, useRow).
 */
export function savedCopyAt(q: { data?: unknown; isError: boolean; dataUpdatedAt: number }): CopyTime | null {
  if (isCopy(q.data)) return copies.get(q.data as object)!;
  return q.isError && q.data !== undefined ? q.dataUpdatedAt : null;
}
